/**
 * Selvhelbredende feilgrense rundt regattasiden.
 *
 * En regatta-nedtelling må aldri «dø» permanent (krever full reload) hvis en
 * transient render-feil oppstår — f.eks. når starttiden justeres mens siden er
 * åpen. Ved en feil: rapporter til serverloggen, vis en kompakt melding, og
 * remonter innholdet automatisk etter noen sekunder (opptil 3 ganger). Gir vi
 * opp, vises en «last på nytt»-knapp. Brukes med key={slug} så hver regatta får
 * en frisk montering (ingen tilstand som henger igjen mellom to regattaer).
 */
import { Component, type ReactNode } from 'react';
import { reportClientError } from '../lib/reportError.ts';

const MAX_RETRIES = 3;
const RETRY_MS = 3000;

export class RaceContentBoundary extends Component<
  { children: ReactNode },
  { error: Error | null; retries: number }
> {
  state: { error: Error | null; retries: number } = { error: null, retries: 0 };
  private timer: number | null = null;

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: unknown) {
    console.error('Regattaside-feil:', error, info);
    reportClientError(error, (info as { componentStack?: string } | null)?.componentStack);
    if (this.state.retries < MAX_RETRIES && this.timer == null) {
      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.setState((s) => ({ error: null, retries: s.retries + 1 }));
      }, RETRY_MS);
    }
  }

  componentWillUnmount() {
    if (this.timer != null) clearTimeout(this.timer);
  }

  render() {
    if (!this.state.error) return this.props.children;
    const gaveUp = this.state.retries >= MAX_RETRIES;
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-3 p-6 text-center">
        <div className="text-3xl">⚠️</div>
        <p className="text-sm text-slate-300 max-w-xs">
          {gaveUp
            ? 'Klarte ikke å vise regattaen. Last siden på nytt.'
            : 'Midlertidig feil i visningen — henter på nytt…'}
        </p>
        {gaveUp ? (
          <button
            onClick={() => location.reload()}
            className="mt-1 px-4 py-2 bg-sky-600 hover:bg-sky-500 rounded-lg text-sm font-semibold"
          >
            Last på nytt
          </button>
        ) : (
          <button
            onClick={() => this.setState({ error: null })}
            className="mt-1 text-xs text-sky-400 hover:text-sky-300"
          >
            Prøv nå
          </button>
        )}
      </div>
    );
  }
}
